/**
 * Bridges a mounted chart tile's live SVG — only reachable through
 * ChartRenderer's imperative ref inside `ChartTileView` — to the tile kebab's
 * image-export item, which renders in a different subtree (`DashboardTab`'s
 * tileMenuItems). A chart tile registers its `getSvgContent` accessor on mount
 * and clears it on unmount, keyed by the tile's runtime key; the menu reads it
 * by the same key. Mirrors the `setTileDangerousConfirmer` bridge pattern.
 *
 * Keyed by a per-key STACK rather than a single accessor: the grid body and
 * the `TileExpandDialog` overlay can both mount a `ChartTileView` for the
 * SAME tile at once, so a naive single-slot map would have the later mount
 * clobber the earlier one and the earlier unmount's cleanup then delete the
 * survivor's registration. Registering pushes; unregistering removes that
 * exact accessor (not the whole key); the reader takes the most recently
 * registered (top-of-stack) accessor.
 */
const registry = new Map<string, Array<() => string | null>>();

export const registerChartSvgSource = (key: string, getSvgContent: () => string | null): void => {
  const stack = registry.get(key);
  if (stack) {
    stack.push(getSvgContent);
  } else {
    registry.set(key, [getSvgContent]);
  }
};

export const unregisterChartSvgSource = (
  key: string,
  getSvgContent: () => string | null,
): void => {
  const stack = registry.get(key);
  if (!stack) return;
  const index = stack.lastIndexOf(getSvgContent);
  if (index === -1) return;
  stack.splice(index, 1);
  if (stack.length === 0) registry.delete(key);
};

/** The chart's current SVG, or null when the tile has no chart mounted (no data). */
export const getChartSvgContent = (key: string): string | null => {
  const stack = registry.get(key);
  if (!stack || stack.length === 0) return null;
  return stack[stack.length - 1]() ?? null;
};
